import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { ShapeElement } from "../Components/Elements/Graphics/ShapeElement";
import { MachineExtended } from "./MachineExtended";
import { PlayList } from "../Components/Sounds/PlayList";
import { CustomUtils } from "../Utils/CustomUtils";
import { Symbol } from "../GameComponents/Symbol";
import { AppContext } from "../../Common/AppContext";
import { ModuleManager } from "../Managers/ModuleManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { Symbols } from "../GameComponents/Symbols";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { WinAnimatorBonus } from "./WinAnimatorBonus";
import { WinAnimatorExtended } from "./WinAnimatorExtended";

/**
 * Machine handle reels and symbols for reels rotation.
 * Machine define behaviour for complete flow of reel rotation.
 * @class Machine
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class MachineBonus extends MachineExtended {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private static readonly ReelsOrder: Array<number> = [0, 5, 10, 1, 6, 11, 2, 7, 12, 3, 8, 13, 4, 9, 14];

    private static readonly StickyLastReelStopDelay: number = 20;

    private static readonly BonusSymbolIds: Array<number> = [12, 13, 14, 15];

    private static readonly BonusGrandTeasingDuration: number = 180;

    private offsetX: number = 1;

    private offsetY: number = 8;

    private shapeElementNode: Element;

    private activeReelsOrder: Array<number>;

    private fullReels: Array<number> = [0, 0, 0, 0, 0];

    protected multiplierIndex: Array<number> = [0, 0, 0, 0, 0];

    private canTease: Array<Array<number>>;

    private breakTeasing: boolean;

    private remainingRespins: number;

    private teasingContainer: ContainerElement;

    private _winAnimatorBonus: WinAnimatorBonus;

    //emit machineStopped with delay
    protected _machineStopTimer: FrameTimerEventEmitter;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.canTease = [];
        this.shapeElementNode = null;

        this._machineStopTimer = CreateFrameTimer("BonusMachineStoppedDelay");
        this._machineStopTimer.on(FrameTimerEvents.Complete, this.onMachineStoppedDelayed.bind(this));

        this.spinBitmask = 0b111111111111111;
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    public create(node: Element): void {
        super.create(node);

        if (node.hasAttribute(MachineExtendedAttributes.REEL_MASK)) {
            if (
                node.firstElementChild != null &&
                node.firstElementChild.nodeName == node.getAttribute(MachineExtendedAttributes.REEL_MASK)
            ) {
                this.shapeElementNode = node.firstElementChild;
            }
        }
    }

    //******************************************************************************************************************
    //  OVERRIDES
    //******************************************************************************************************************

    protected createReels(reels: Array<Array<number>>, reelValues?: Array<Array<number>>): void {
        super.createReels(reels, reelValues);

        //add mask
        for (let i: number = 0; i < this._reelCount; i++) {
            const reel = this._reels[i];
            const orderIndex = MachineBonus.ReelsOrder.indexOf(i);
            const nextReel =
                orderIndex < this._reelCount - 1 ? this._reels[MachineBonus.ReelsOrder[orderIndex + 1]] : null;
            const previousReel = orderIndex > 0 ? this._reels[MachineBonus.ReelsOrder[orderIndex - 1]] : null;

            if (reel != null) {
                reel.updateReelSettings(
                    previousReel,
                    nextReel,
                    this._startDelay * orderIndex,
                    this._stopDelay * orderIndex,
                    true,
                );

                if (this.shapeElementNode != null) {
                    const maskElement = new ShapeElement();
                    maskElement.createByNode(this.shapeElementNode);
                    maskElement.x = this._reelX[i] + this.offsetX + 1;
                    maskElement.y = this._reelY[i] + this.offsetY - (window["QUALITY"] === "QHD" ? 2 : 0);

                    reel.mask = maskElement;
                    this.addChild(reel.mask);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        reelValues?: Array<Array<number>>,
        teasing?: ContainerElement,
        reelsRespin?: Array<Array<number>>,
        winAnimator?: WinAnimatorExtended,
        winAnimatorBonus?: WinAnimatorBonus,
    ): void {
        super.initialize(symbols, reels, reelValues, teasing);

        this.teasingContainer = teasing;
        this._winAnimatorBonus = winAnimatorBonus;
    }

    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
        spinBitmask: number = 0,
    ): void {
        this.activeReelsOrder = [];
        this.canTease = [];
        this.breakTeasing = false;
        this.remainingRespins = CustomUtils.getRemainingRespins() - 1;
        this.fullReels = [0, 0, 0, 0, 0];
        this.multiplierIndex = [0, 0, 0, 0, 0];
        this._winAnimatorBonus.multiplierTeasingIndex = -1;

        super.spinStart(longRotation, useTransform, currentSymbols, spinBitmask);

        let reelIndex = 0;
        let firstReel = -1;
        if (this._reels != null) {
            let delayIndex = 0;

            for (let i = 0; i < this._reelCount; i++) {
                const reel = this._reels[MachineBonus.ReelsOrder[i]];
                if (i % 3 === 0 && i > 0) {
                    reelIndex++;
                }

                if (!reel.isSkipped()) {
                    this.activeReelsOrder.push(MachineBonus.ReelsOrder[i]);
                    reel.setStopDelay(this._stopDelay * delayIndex);
                    delayIndex++;

                    if (firstReel === -1) {
                        firstReel = i;
                    }
                } else {
                    this.fullReels[reelIndex]++;
                }
            }
        }

        if (firstReel !== -1) {
            this.canTease = this.checkTeasingAllReels(false, this.activeReelsOrder);
            if (this.canTease[0] != null) {
                this.tease(-1);
            }
        }
    }

    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (DEBUG) {
            this.log("spinStop " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !ModuleManager.bonusActive() &&
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            this._forceStop = force;

            this.canTease = this.checkTeasingAllReels(force, this.activeReelsOrder);

            let wasTease = false;
            let wasFirstReel = false;
            let teasingDelay = 0;
            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    let index = MachineBonus.ReelsOrder[i];

                    if (this._reels[index] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._stoppingReelsCounter++;
                            this._reels[index].setData(this._stoppedSymbolsArray[index]);

                            let activeReelIndex = this.activeReelsOrder.indexOf(index);

                            if (activeReelIndex != -1) {
                                if (this.canTease[activeReelIndex] == null) {
                                    if (wasFirstReel) {
                                        teasingDelay += this._stopDelay;
                                    }
                                    wasFirstReel = true;
                                } else {
                                    teasingDelay += MachineBonus.BonusGrandTeasingDuration;
                                }
                            }

                            //if reel will not be teasing, call spinStop, otherwise be handle spinStop in tease method
                            if (activeReelIndex === -1) {
                                this._reels[index].spinStop(force, teasingDelay);
                            } else if (this.canTease[activeReelIndex] == null && wasTease === false) {
                                this._reels[index].spinStop(force, teasingDelay);
                            } else {
                                wasTease = true;

                                if (this.canTease[activeReelIndex] == null) {
                                    this._reels[index].spinStop(force, teasingDelay);
                                } else {
                                    this._reels[index].spinTease(this.canTease[activeReelIndex], teasingDelay);
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }

            //if we tease first reel, call teasing manually
            // if (!force && this.canTease[0] != null) {
            //     this.tease(-1);
            // }
        }
    }

    public onReelStopped(reelIndex: number): void {
        if (!this._reels[reelIndex].isSkipped()) {
            PlayList.setPan("SND_BaseRotationEnd" + (reelIndex % 5), 5, reelIndex % 5);
            PlayList.play("SND_BaseRotationEnd" + (reelIndex % 5));
            this.tease(reelIndex);
        }

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;
        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            if (CustomUtils.isBonusSymbol(this._stoppedSymbolsArray[reelIndex].symbols[0])) {
                this._machineStopTimer.start(MachineBonus.StickyLastReelStopDelay);
            } else {
                this.onMachineStoppedDelayed();
            }
        }
    }

    protected onMachineStoppedDelayed(): void {
        this._machineStopTimer.stop();

        PlayList.stop("SND_BaseRotation", 10);
        if (this._legalStopTimer != null) {
            clearTimeout(this._legalStopTimer);
        }
        this._stopTimer.stop();

        if (DEBUG) {
            const old = AppContext.gameConfiguration.MinGameRoundDuration;
            const current = AppContext.gameConfiguration.GameRoundMinSpan;
            const minDuration = current > 0 ? current : old > 0 ? old : 0;
            const spinDuration = Date.now() - this._startSpinTime;

            if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                this.log("Spin time duration: " + spinDuration + " ms");
            } else {
                console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
            }
        }

        this.emit(Machine.MachineStopped);
        this._reelsContainer.visible = false;
    }

    protected checkTeasingAllReels(force: boolean, activeReels: Array<number>): Array<Array<number>> {
        let canTease: Array<Array<number>> = [];

        if (activeReels != null) {
            for (let i = 0; i < activeReels.length; i++) {
                const currentReelIndex = activeReels[i];
                const result = force ? null : this.checkTeasingBonus(currentReelIndex, activeReels);

                canTease.push(result);
            }
        }

        return canTease;
    }

    protected checkTeasingBonus(reelIndex: number, activeReels: Array<number> = []): Array<number> {
        let canTease = false;

        // if multiplier
        let index = reelIndex % 5;
        if (index === 0 && this.fullReels[0] === 2 && this.fullReels[1] === 3 && this.fullReels[2] === 3) {
            canTease = true;
        } else if (index === 1 && this.fullReels[0] === 3 && this.fullReels[1] === 2 && this.fullReels[2] === 3) {
            canTease = true;
        } else if (index === 2 && this.fullReels[0] === 3 && this.fullReels[1] === 3 && this.fullReels[2] === 2) {
            canTease = true;
        } else if (
            index === 3 &&
            this.fullReels[0] === 3 &&
            this.fullReels[1] === 3 &&
            this.fullReels[2] === 3 &&
            this.fullReels[3] === 2
        ) {
            canTease = true;
        } else if (
            index === 4 &&
            this.fullReels[0] === 3 &&
            this.fullReels[1] === 3 &&
            this.fullReels[2] === 3 &&
            this.fullReels[3] === 3 &&
            this.fullReels[4] === 2
        ) {
            canTease = true;
        }

        if (canTease) {
            if (index <= 2) {
                if (this.fullReels[3] === 3 && this.fullReels[4] === 3) {
                    this.multiplierIndex[index] = 5;
                } else if (this.fullReels[3] === 3) {
                    this.multiplierIndex[index] = 4;
                } else {
                    this.multiplierIndex[index] = 3;
                }
            } else if (index === 3) {
                if (this.fullReels[4] === 3) {
                    this.multiplierIndex[index] = 5;
                } else {
                    this.multiplierIndex[index] = 4;
                }
            } else {
                this.multiplierIndex[index] = 5;
            }
        }

        if (this.fullReels[index] < 3 && this._stoppedSymbolsArray[reelIndex].overlay) {
            this.fullReels[index]++;
        }

        if (canTease) {
            return MachineBonus.BonusSymbolIds;
        }

        //if last spot
        if (activeReels.length == 1) {
            return MachineBonus.BonusSymbolIds;
        }

        //remain last spot
        if (activeReels.indexOf(reelIndex) == activeReels.length - 1) {
            canTease = true;

            for (let i = 0; i < activeReels.length - 1; i++) {
                const currentIndex = activeReels[i];

                if (!CustomUtils.isBonusSymbol(this._stoppedSymbolsArray[currentIndex].symbols[0])) {
                    canTease = false;
                    break;
                }
            }
        }

        return canTease ? MachineBonus.BonusSymbolIds : null;
    }

    protected tease(reelIndex: number): void {
        if (reelIndex == -1) {
            //tease last spot
            if (this.activeReelsOrder.length == 1) {
                this.reelTeasingGoToCase(this.activeReelsOrder[0], "TEASE");

                if (BaseView.BonusCounters != null) {
                    BaseView.BonusCounters.counterGoToCase(3, "TEASE_POP");
                }
            } else {
                //teasing animation to all active reels
                this.reelTeasingGoToCase(this.activeReelsOrder[0], "TEASE");
            }
            PlayList.play("SND_BaseTeaser");
            this._winAnimatorBonus.onSameSymbolsOnReelTeasingStart(
                this.multiplierIndex[this.activeReelsOrder[0] % 5],
                this.activeReelsOrder[0],
            );
        } else {
            //check teasing only for active reels
            if (this.activeReelsOrder.indexOf(reelIndex) > -1) {
                //last reel stopped (machine stopped)
                if (this.activeReelsOrder.indexOf(reelIndex) == this.activeReelsOrder.length - 1) {
                    //disable all teasing animations
                    this.allTeasingGoToCase("HIDE");

                    PlayList.stop("SND_BaseTeaser", 10);

                    //disable tease for counter if last symbol was not bonus
                    if (
                        CustomUtils.isLastBonusSpin() &&
                        !CustomUtils.isBonusSymbol(this._stoppedSymbolsArray[reelIndex].symbols[0])
                    ) {
                        if (BaseView.BonusCounters != null) {
                            BaseView.BonusCounters.counterGoToCase(3, "BONUS");
                        }
                    }
                } else {
                    const indexOfNextActiveReel = this.activeReelsOrder.indexOf(reelIndex) + 1;
                    const nextReelIndex = this.activeReelsOrder[indexOfNextActiveReel];
                    const nextTeasingSymbols = this.canTease[indexOfNextActiveReel];
                    this.reelTeasingGoToCase(reelIndex, "HIDE");

                    PlayList.stop("SND_BaseTeaser", 10);

                    if (nextTeasingSymbols != null) {
                        this.reelTeasingGoToCase(nextReelIndex, "TEASE");
                        PlayList.play("SND_BaseTeaser");
                        this._winAnimatorBonus.onSameSymbolsOnReelTeasingStart(
                            this.multiplierIndex[nextReelIndex % 5],
                            nextReelIndex,
                        );
                        if (nextTeasingSymbols != null && indexOfNextActiveReel == this.activeReelsOrder.length - 1) {
                            if (BaseView.BonusCounters != null) {
                                BaseView.BonusCounters.counterGoToCase(3, "TEASE_POP");
                            }
                        }
                    }
                }
            }
        }
    }

    private allTeasingGoToCase(caseType: string, onlyActive: boolean = false): void {
        for (let i = 0; i < MachineBonus.ReelsOrder.length; i++) {
            const reelIndex = MachineBonus.ReelsOrder[i];

            if (onlyActive && this.activeReelsOrder.indexOf(reelIndex) == -1) {
                continue;
            }

            this.reelTeasingGoToCase(reelIndex, caseType);
        }
    }

    private reelTeasingGoToCase(reelIndex: number, caseType: string): void {
        if (this.teasingContainer != null) {
            const teasingElement = <SwitchElement>this.teasingContainer.childs["Teasing_" + reelIndex];

            if (
                caseType == "HIDE" &&
                (teasingElement.getCurrentCase() == "EMPTY" || teasingElement.getCurrentCase() == "SHOW_2")
            ) {
                return;
            }

            teasingElement.goToCase(caseType);
        }
    }
}

/**
 * Can inherit attributes from BaseElement
 */
export const MachineExtendedAttributes = {
    REEL_MASK: "ReelMask",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineBonus.ts
 **/